import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {observer} from 'mobx-react-lite';
import {useNavigate} from 'react-router-dom';
import {MapContainer, Marker, Popup, TileLayer, useMap, useMapEvents} from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import appStore from '../../storage/AppStore';
import {getProduct} from '../../common/config';
import '../../common/club_search.css';

const START_CENTER = [42.87676, 74.602019];
// The connection status comes from the API, never from whether a logo exists.
const isUnconnected = company => company?.is_connected === false || company?.status === 'pending';
const unconnectedLabel = () => {
  const product = getProduct() === 'course1' ? 'Course #1' : 'Club #1';
  const labels = {
    ru: 'Ещё не подключён к {product}',
    kg: '{product} системасына азырынча кошула элек',
    kz: '{product} жүйесіне әлі қосылмаған',
    uz: 'Hali {product} tizimiga ulanmagan',
    en: 'Not yet connected to {product}'
  };
  const template = appStore.tr?.club_search_not_connected || labels[appStore.user?.language] || labels.ru;
  return template.replace('{product}', product);
};
const unconnectedStyle = {display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '.77rem', lineHeight: 1.35, color: 'var(--brand-hint)', marginTop: '5px'};

const normalCoords = point => {
  const position = point?.location && typeof point.location === 'object' ? point.location : point;
  if (!position || position.lat == null || (position.lng == null && position.lon == null)) return null;
  const lat = Number(position.lat), lng = Number(position.lng ?? position.lon);
  return Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 ? [lat, lng] : null;
};
const longitude = x => ((x + 180) % 360 + 360) % 360 - 180;
const round = x => Number(x.toFixed(6));
const mainIcon = L.divIcon({className: 'csm-pin', html: '<span class="csm-pin-inner"><i class="pi pi-map-marker"></i></span>', iconSize: [38, 44], iconAnchor: [19, 43], popupAnchor: [0, -39]});
const iconCache = new Map();
const escapeHtml = value => String(value).replace(/[&<>"']/g, ch => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[ch]));
const sportIcon = sport => {
  const emoji = sport?.icon || sport?.data?.icon;
  return typeof emoji === 'string' ? emoji.trim() : '';
};
const markerIcon = emoji => {
  if (!emoji) return mainIcon;
  if (!iconCache.has(emoji)) iconCache.set(emoji, L.divIcon({className: 'csm-pin', html: `<span class="csm-pin-inner"><span style="display:flex;align-items:center;justify-content:center;width:26px;height:26px;font-size:22px;line-height:1;transform:rotate(45deg)" aria-hidden="true">${escapeHtml(emoji)}</span></span>`, iconSize: [38, 44], iconAnchor: [19, 43], popupAnchor: [0, -39]}));
  return iconCache.get(emoji);
};
const normalizedText = text => String(text || '').trim().toLocaleLowerCase();
const matchesSportName = (sport, term) => {
  if (!sport || !term) return false;
  const names = [sport.id, sport.name, ...Object.values(sport.data?.names || {})];
  return names.some(name => normalizedText(name).includes(term));
};
const iconForClub = (company, sportCatalog, selectedSportId, selectedSport) => {
  const clubSports = Array.isArray(company.sports) ? company.sports : [];
  const details = item => sportCatalog.get(String(item?.id || '')) || item;
  let chosen = null;
  if (selectedSportId) chosen = sportCatalog.get(String(selectedSportId)) || clubSports.find(item => String(item.id) === String(selectedSportId));
  else if (selectedSport) {
    const term = normalizedText(selectedSport);
    chosen = clubSports.map(details).find(item => matchesSportName(item, term)) || [...sportCatalog.values()].find(item => matchesSportName(item, term));
  } else chosen = clubSports[0] ? details(clubSports[0]) : null;
  return markerIcon(sportIcon(chosen));
};
const clusterIcon = cluster => L.divIcon({className: 'csm-cluster', html: `<span>${cluster.getChildCount()}</span>`, iconSize: [43, 43], iconAnchor: [21, 21]});

// Unlike fitBounds(points), the map's visible bounds drive the search.
const ViewportObserver = ({onBoundsChange}) => {
  const previousKey = useRef('');
  const report = useCallback(map => {
    const b = map.getBounds(), c = map.getCenter();
    if (!b.isValid()) return;
    const south = round(Math.max(-90, b.getSouth())), north = round(Math.min(90, b.getNorth()));
    if (south >= north) return;
    const world = b.getEast() - b.getWest() >= 360;
    const bounds = {south, west: world ? -180 : round(longitude(b.getWest())), north, east: world ? 180 : round(longitude(b.getEast()))};
    const viewport = {bounds, center: [c.lat, c.lng], zoom: map.getZoom()};
    const key = JSON.stringify({...bounds, zoom: viewport.zoom});
    if (key !== previousKey.current) {previousKey.current = key; onBoundsChange(viewport);}
  }, [onBoundsChange]);
  const map = useMapEvents({moveend(e) {report(e.target);}, zoomend(e) {report(e.target);}, resize(e) {report(e.target);}});
  useEffect(() => {map.whenReady(() => report(map));}, [map, report]);
  return null;
};

const ClubsMap = ({companies = [], loading = false, onBoundsChange, onSearch, initialCenter, initialZoom, selectedSportId = '', selectedSport = ''}) => {
  const navigate = useNavigate();
  const points = useMemo(() => {
    const found = [];
    (Array.isArray(companies) ? companies : []).forEach(company => {
      const locations = Array.isArray(company.locations) && company.locations.length ? company.locations : company.location ? [company.location] : [];
      locations.forEach((location, index) => {
        const coords = normalCoords(location);
        if (coords) found.push({key: `${company.id}:${location.id ?? `fallback-${index}`}`, company, location, coords});
      });
    });
    return found;
  }, [companies]);
  const text = appStore.tr || {};
  const sportCatalog = new Map((Array.isArray(appStore.sports) ? appStore.sports : []).map(sport => [String(sport.id), sport]));
  const center = normalCoords({lat: initialCenter?.[0], lng: initialCenter?.[1]}) || START_CENTER;
  const zoom = Number.isFinite(Number(initialZoom)) && initialZoom >= 2 && initialZoom <= 19 ? Number(initialZoom) : 12;

  return <div className={`csm-wrap${loading ? ' csm-pending' : ''}`} style={{position: 'relative'}}>
    <MapContainer center={center} zoom={zoom} scrollWheelZoom={false} className="csm-map" aria-label={text.club_search_view_map}>
      <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
      <ViewportObserver onBoundsChange={onBoundsChange}/>
      <MarkerClusterGroup chunkedLoading showCoverageOnHover={false} maxClusterRadius={50} spiderfyOnMaxZoom zoomToBoundsOnClick iconCreateFunction={clusterIcon}>
        {points.map(point => <Marker key={point.key} position={point.coords} icon={iconForClub(point.company, sportCatalog, selectedSportId, selectedSport)}>
          <Popup className="csm-popup"><div className="csm-popup-inner">
            <strong className="csm-popup-name">{point.company.name}</strong>
            {(point.location.name || point.location.address || point.company.address) && <span className="csm-popup-address">
              {point.location.name && <span>{point.location.name}</span>}
              {(point.location.address || point.company.address) && <span>{point.location.address || point.company.address}</span>}
            </span>}
            {point.company.sports?.length > 0 && <div className="csm-popup-sports">{point.company.sports.slice(0, 3).map(sport => <span key={sport.id}>{sport.name}</span>)}</div>}
            {isUnconnected(point.company) && <div className="csm-unconnected-note" style={unconnectedStyle}><i className="pi pi-info-circle" aria-hidden="true"/><span>{unconnectedLabel()}</span></div>}
            <button type="button" className="csm-open" onClick={() => navigate(`/club/${point.company.id}`)}>{text.show_more}<i className="pi pi-arrow-right" aria-hidden="true"/></button>
          </div></Popup>
        </Marker>)}
      </MarkerClusterGroup>
    </MapContainer>
    {onSearch && <button type="button" className="csd-submit" disabled={loading} onClick={onSearch}
      style={{position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, width: 'max-content', maxWidth: 'calc(100% - 24px)'}}>
      <i className={loading ? 'pi pi-spin pi-spinner' : 'pi pi-search'} aria-hidden="true"/>
      {text.club_search_search_this_area || text.club_search_button || 'Поиск в этой области'}
    </button>}
    {!points.length && <div role="status" style={{padding: '12px', textAlign: 'center'}}><i className={loading ? 'pi pi-spin pi-spinner' : 'pi pi-map-marker'} aria-hidden="true"/> {loading ? text.loading : text.club_search_map_no_locations}</div>}
  </div>;
};

export default observer(ClubsMap);
